/**
 * StudentLayout v3 — macOS-style floating dock experience.
 *
 * Desktop: vertical pill-shaped floating dock (glassmorphism, icons-only,
 * hover tooltips, rose accent glow on active). The dock sits as an overlay
 * and the main content simply inherits a left padding so it never scrolls
 * underneath. Mobile: the same dock becomes a centered bottom pill.
 *
 * The TopBar is gone from this layout — notifications + profile chip now
 * live in a small floating corner cluster so the canvas feels uncluttered
 * and the greeting hero can be the true entry point to the experience.
 */
import React, { useMemo, useState, useRef, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import AnimatedOutlet from "./AnimatedOutlet";
import {
  LayoutDashboard, BookOpen, Trophy, UserCheck,
  Users, LogOut, Repeat2, Layers, Flame, Medal,
  Settings as SettingsIcon, ChevronRight, ChevronLeft, ChevronUp,
  Search, MessagesSquare,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import NotificationBell from "./NotificationBell";
import CommandPalette, { openCommandPalette } from "./CommandPalette";
import FocusMode from "./FocusMode";
import ReportFab from "./ReportFab";
import "../lms.css";
import "../portal.css";

const DOCK_GROUPS = [
  {
    items: [
      { to: "/student",            icon: LayoutDashboard, label: "Dashboard",    end: true },
    ],
  },
  {
    items: [
      { to: "/student/study",      icon: BookOpen,        label: "Study" },
      { to: "/student/revision",   icon: Repeat2,         label: "Revision" },
      { to: "/student/flashcards", icon: Layers,          label: "Flashcards" },
    ],
  },
  {
    items: [
      { to: "/student/streak",             icon: Flame,   label: "Streak" },
      { to: "/student/streak-leaderboard", icon: Medal,   label: "Streak ranks" },
      { to: "/student/leaderboard",        icon: Trophy,  label: "Leaderboard" },
    ],
  },
  {
    items: [
      { to: "/student/mentor",     icon: UserCheck,      label: "Mentor" },
      { to: "/student/discussion", icon: MessagesSquare, label: "Discussion" },
      { to: "/student/community",  icon: Users,          label: "Community" },
    ],
  },
];

const FLAT = DOCK_GROUPS.flatMap((g) => g.items);

function DockItem({ to, icon: Icon, label, end, onNavigate, badge }) {
  const testid = `student-nav-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) => `dock-item focus-ring ${isActive ? "active" : ""}`}
      data-testid={testid}
      aria-label={label}
    >
      <div className="relative">
        <Icon size={18} strokeWidth={1.75} />
        {badge > 0 && (
          <span
            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full flex items-center justify-center text-[9.5px] font-bold leading-none tabular-nums"
            style={{ background: "#dc2626", color: "white", boxShadow: "0 0 0 2px #ffffff" }}
            data-testid={`${testid}-badge`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="dock-item__label">{label}</span>
      <span className="dock-item__tip">{label}</span>
    </NavLink>
  );
}

function Dock({ variant = "desktop", expanded, onToggle, onNavigate, badges = {} }) {
  const isBottom = variant === "mobile";
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const itemsRef = useRef(null);
  const navRef = useRef(null);
  const location = useLocation();
  const [overflowState, setOverflowState] = useState({ has: false, atStart: true, atEnd: false });

  // Iter 197.2 — auto-scroll the active tab into view IF it's off-screen,
  // using `inline: "nearest"` so we scroll the minimum amount possible.
  // Previously used `"center"` which centred the active tab and pushed
  // the first 2–3 items off-screen to the left on routes where the
  // active tab was mid-list (e.g. Revision, Streak ranks). Now the row
  // always starts at scrollLeft=0 unless the active tab is beyond the
  // right edge — matches iOS Music / Spotify tab behaviour.
  useEffect(() => {
    if (!isBottom || !itemsRef.current) return;
    const t = setTimeout(() => {
      const el = itemsRef.current?.querySelector(".dock-item.active");
      if (el && el.scrollIntoView) {
        try {
          el.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
        } catch (_e) {
          el.scrollIntoView(false);
        }
      }
    }, 40);
    return () => clearTimeout(t);
  }, [isBottom, location.pathname]);

  // Iter 197.1 — track horizontal-overflow state on the mobile dock so
  // the CSS can show/hide the left/right swipe hints. `has-overflow`
  // is added when scrollWidth > clientWidth; `at-scroll-start` and
  // `at-scroll-end` toggle as the row is swiped so the arrow at the
  // reached edge fades out.
  useEffect(() => {
    if (!isBottom) return undefined;
    const el = itemsRef.current;
    if (!el) return undefined;
    const recompute = () => {
      const has = el.scrollWidth - el.clientWidth > 4;
      const atStart = el.scrollLeft <= 2;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      setOverflowState((prev) =>
        prev.has === has && prev.atStart === atStart && prev.atEnd === atEnd
          ? prev
          : { has, atStart, atEnd }
      );
    };
    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recompute) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", recompute);
    return () => {
      el.removeEventListener("scroll", recompute);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [isBottom, expanded]);

  // Desktop: horizontal swipe to expand/collapse the rail.
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 36 && !expanded) onToggle(true);
    else if (dx < -36 && expanded) onToggle(false);
    touchStartX.current = null;
  };

  // Mobile: vertical swipe-up to expand the bottom sheet, swipe-down to
  // collapse. Iter 192.3 — track BOTH axes so a mostly-horizontal swipe
  // (used for the tab scroll) never fires the expand/collapse gesture.
  const handleSheetTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleSheetTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const dx = e.changedTouches[0].clientX - (touchStartX.current || 0);
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // Only expand/collapse when the gesture is DOMINANTLY vertical.
    if (Math.abs(dy) > Math.abs(dx) * 1.4) {
      if (dy < -40 && !expanded) onToggle(true);
      else if (dy > 40 && expanded) onToggle(false);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const navExtraClasses = isBottom
    ? `${overflowState.has ? "has-overflow" : ""} ${overflowState.atStart ? "at-scroll-start" : ""} ${overflowState.atEnd ? "at-scroll-end" : ""}`
    : "";

  return (
    <nav
      ref={navRef}
      className={`floating-dock ${isBottom ? "floating-dock--bottom mobile-only" : "desktop-only"} ${expanded ? "expanded" : ""} ${navExtraClasses}`}
      data-testid={isBottom ? "student-dock-mobile" : "student-dock"}
      data-expanded={expanded ? "true" : "false"}
      aria-label="Primary navigation"
      onTouchStart={isBottom ? handleSheetTouchStart : handleTouchStart}
      onTouchEnd={isBottom ? handleSheetTouchEnd : handleTouchEnd}
    >
      {!isBottom && (
        <>
          <Link to="/student" className="floating-dock__brand focus-ring" aria-label="LogicsWallah Home" data-testid="student-dock-brand">
            <img
              src="/brand/logo.png"
              alt="LogicsWallah"
              className="floating-dock__brand-img"
              draggable="false"
            />
          </Link>
          <button
            type="button"
            onClick={() => onToggle(!expanded)}
            className="dock-handle focus-ring"
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            data-testid="student-dock-toggle"
          >
            {expanded ? <ChevronLeft size={13} /> : <ChevronRight size={13} />}
          </button>
          <div className="floating-dock__divider" />
        </>
      )}

      {/* Mobile-only expand handle — chevron arrow that flips when open.
          Tapping it toggles the bottom sheet; the sheet itself reveals
          labels alongside icons. */}
      {isBottom && (
        <button
          type="button"
          onClick={() => onToggle(!expanded)}
          className="dock-sheet-handle focus-ring"
          aria-label={expanded ? "Collapse menu" : "Expand menu"}
          data-testid="student-mobile-dock-toggle"
        >
          <ChevronUp size={14} className={`dock-sheet-handle__icon ${expanded ? "is-flipped" : ""}`} />
        </button>
      )}

      {/* Iter 197.1 — subtle left/right swipe hints. Anchored to the nav
          (not the scrolling row) so they never move with the icons.
          Visibility is driven by CSS from `.has-overflow`, `.at-scroll-start`,
          `.at-scroll-end` classes above. */}
      {isBottom && (
        <>
          <span className="dock-swipe-hint dock-swipe-hint--left" aria-hidden="true" />
          <span className="dock-swipe-hint dock-swipe-hint--right" aria-hidden="true" />
        </>
      )}

      <div ref={itemsRef} className={`dock-items ${isBottom ? "dock-items--bottom" : ""}`}>
        {DOCK_GROUPS.map((group, gIdx) => (
          <React.Fragment key={gIdx}>
            {group.items.map((item) => <DockItem key={item.to} {...item} onNavigate={onNavigate} badge={badges[item.to] || 0} />)}
            {!isBottom && gIdx < DOCK_GROUPS.length - 1 && <div className="floating-dock__divider" />}
          </React.Fragment>
        ))}

        {!isBottom && (
          <>
            <div className="floating-dock__divider" />
            <DockItem to="/student/settings" icon={SettingsIcon} label="Settings" onNavigate={onNavigate} />
          </>
        )}
        {/* Mobile: include Settings inline so the expanded sheet exposes every nav */}
        {isBottom && (
          <DockItem to="/student/settings" icon={SettingsIcon} label="Settings" onNavigate={onNavigate} />
        )}
      </div>
    </nav>
  );
}

function CornerCluster({ user, onLogout }) {
  const initial = (user?.name || "L").charAt(0).toUpperCase();
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");
  return (
    <div
      className="hidden md:flex fixed top-5 right-5 z-40 items-center gap-2"
      data-testid="student-corner-cluster"
    >
      <button
        type="button"
        onClick={openCommandPalette}
        className="flex items-center gap-2 h-10 pl-3 pr-2 rounded-full surface-soft press-btn focus-ring"
        style={{ boxShadow: "0 6px 18px -10px rgba(11,11,13,0.15)", color: "var(--px-ink-mute)" }}
        aria-label="Open command palette"
        data-testid="student-cmdk-btn"
      >
        <Search size={13} strokeWidth={2} />
        <span className="text-[11.5px] font-semibold" style={{ color: "var(--px-ink-soft)" }}>Search</span>
        <kbd className="text-[9.5px] font-mono font-bold px-1.5 py-0.5 rounded"
             style={{ background: "var(--px-paper-soft)", color: "var(--px-ink-mute)", border: "1px solid var(--px-line)" }}>
          {isMac ? "⌘K" : "Ctrl K"}
        </kbd>
      </button>
      <NotificationBell testid="student-topbar-notifs" />
      <Link
        to="/student/profile"
        className="flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full surface-soft press-btn focus-ring"
        style={{ boxShadow: "0 6px 18px -10px rgba(11,11,13,0.15)" }}
        data-testid="student-topbar-profile"
      >
        {user?.avatar ? (
          <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold"
            style={{ background: "var(--px-ink)", color: "#fff" }}
          >{initial}</div>
        )}
        <span className="text-[12.5px] font-semibold" style={{ color: "var(--px-ink)" }}>
          {(user?.name || "Logician").split(" ")[0]}
        </span>
      </Link>
      <button
        type="button"
        onClick={onLogout}
        className="w-10 h-10 rounded-full flex items-center justify-center surface-soft press-btn focus-ring"
        style={{ color: "var(--px-ink-mute)" }}
        aria-label="Log out"
        data-testid="student-logout"
      >
        <LogOut size={15} strokeWidth={1.75} />
      </button>
    </div>
  );
}

function MobileTopStrip({ user }) {
  return (
    <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-2.5"
         style={{ background: "#ffffff", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--px-line)" }}>
      <Link to="/student" className="flex items-center press-btn focus-ring rounded-lg -ml-1" data-testid="student-mobile-brand" aria-label="LogicsWallah Home">
        <img
          src="/brand/logo.png"
          alt="LogicsWallah"
          className="h-11 w-auto object-contain select-none"
          draggable="false"
        />
      </Link>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={openCommandPalette}
          aria-label="Search"
          data-testid="student-mobile-cmdk"
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "var(--px-surface)", color: "var(--px-ink-soft)", border: "1px solid var(--px-line)" }}
        >
          <Search size={14} strokeWidth={2} />
        </button>
        <NotificationBell testid="student-mobile-notifs" />
        <Link to="/student/profile" data-testid="student-mobile-profile"
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: "var(--px-ink)", color: "#fff", fontWeight: 600, fontSize: 12 }}>
          {(user?.name || "L").charAt(0).toUpperCase()}
        </Link>
      </div>
    </div>
  );
}

export default function StudentLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Dock expand/collapse — remember across sessions, default collapsed
  const [expanded, setExpanded] = useState(() => {
    try { return sessionStorage.getItem("lw_dock_expanded") === "1"; } catch { return false; }
  });
  // Mobile bottom-sheet expansion is its own piece of state — we never want
  // a desktop-set value to leak into mobile and vice versa.
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const toggleExpanded = (val) => {
    setExpanded(val);
    try { sessionStorage.setItem("lw_dock_expanded", val ? "1" : "0"); } catch { /* noop */ }
  };
  // When the user picks a nav item, auto-collapse so they see the full canvas
  const handleNavigate = () => {
    if (expanded) toggleExpanded(false);
    if (mobileExpanded) setMobileExpanded(false);
  };

  // Close the mobile sheet on route change + on `Escape` for keyboard users.
  // Deliberately keyed on pathname ONLY — including `mobileExpanded` in the
  // deps would re-run this effect the instant the state flips to true,
  // immediately snapping it back to false and preventing the sheet from
  // ever opening.
  useEffect(() => { setMobileExpanded(false); }, [pathname]);
  useEffect(() => {
    if (!mobileExpanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMobileExpanded(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileExpanded]);

  // Iter 197 — lock body scroll while the mobile sheet is open. Without this,
  // once the user's finger crosses into the backdrop OR the sheet reaches its
  // scroll edges, the touch gesture bubbles to `body` and the dashboard
  // behind slides out from under the dock, making the dock appear to
  // "disappear" as the user scrolls. Locking the body plus a stable
  // position:fixed dock keeps everything glued in place.
  useEffect(() => {
    if (!mobileExpanded) return undefined;
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.classList.add("lw-scroll-locked");
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      document.body.classList.remove("lw-scroll-locked");
      // Restore scroll position exactly where the user left it.
      window.scrollTo(0, scrollY);
    };
  }, [mobileExpanded]);

  React.useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "var(--px-paper)";
    return () => { document.body.style.background = prev; };
  }, []);

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  // ── Unread Discussion badge — polls every 45s while a student session is active.
  // Auto-zero when the user is currently viewing the discussion area.
  const [discussionUnread, setDiscussionUnread] = React.useState(0);
  React.useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const r = await import("axios").then((m) => m.default.get(`${process.env.REACT_APP_BACKEND_URL}/api/discussion/unread`, { withCredentials: true }));
        if (!stopped) setDiscussionUnread(r.data?.total || 0);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(() => { if (document.hidden) return; tick(); }, 120_000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);
  const badges = React.useMemo(() => {
    const onDiscussion = pathname.startsWith("/student/discussion");
    return { "/student/discussion": onDiscussion ? 0 : discussionUnread };
  }, [discussionUnread, pathname]);

  // When the user navigates to /student/discussion* clear the badge optimistically
  // (the next 45-s poll will reaffirm the real count).
  React.useEffect(() => {
    if (pathname.startsWith("/student/discussion")) setDiscussionUnread(0);
  }, [pathname]);

  // Kept for a11y + potential breadcrumbs in future
  useMemo(() => {
    const match = FLAT.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
    return match?.label || "Portal";
  }, [pathname]);

  return (
    <div className="portal-student min-h-screen">
      <Dock variant="desktop" expanded={expanded} onToggle={toggleExpanded} onNavigate={handleNavigate} badges={badges} />
      <Dock variant="mobile"  expanded={mobileExpanded} onToggle={setMobileExpanded} onNavigate={handleNavigate} badges={badges} />
      {/* Tap-anywhere backdrop that closes the mobile sheet — sits behind the
          dock but above content so the sheet feels like a true overlay. */}
      {mobileExpanded && (
        <div
          className="md:hidden fixed inset-0 z-[45]"
          style={{ background: "rgba(10, 23, 51, 0.42)", backdropFilter: "blur(4px)" }}
          onClick={() => setMobileExpanded(false)}
          data-testid="student-mobile-dock-backdrop"
          aria-hidden="true"
        />
      )}
      <CornerCluster user={user} onLogout={handleLogout} />
      <MobileTopStrip user={user} />
      <CommandPalette />
      <FocusMode />
      <ReportFab />
      <main className="portal-main min-h-screen">
        <AnimatedOutlet />
      </main>
    </div>
  );
}
